import {
  AfterContentInit,
  Component,
  ChangeDetectionStrategy,
  Injector,
  forwardRef,
  input,
  output,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from '@angular/forms';
import { BaseControlValueAccessor } from '../base-control-value-accessor';
import { LookupViewModel } from '@program-suite/data-common';

import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Subscription } from 'rxjs';

interface Options extends LookupViewModel {
  value: any;
}

@Component({
  selector: 'lib-form-dropdown',
  templateUrl: './form-dropdown.component.html',
  styleUrls: ['./form-dropdown.component.scss', '../form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatSelectModule, MatFormFieldModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDropdownComponent),
      multi: true,
    },
  ],
})
export class FormDropdownComponent extends BaseControlValueAccessor<string> implements OnInit, AfterContentInit, OnDestroy {
  label = input.required<string>();
  placeholder = input<string>('');
  // 'always' keeps the label small even when empty, so a row of dropdowns has matching labels
  floatLabel = input<'auto' | 'always'>('auto');
  options = input<LookupViewModel[] | undefined>();
  manualOptions = input<Options[] | undefined>();
  multiple = input<boolean>(false);
  disabledOptions = input<boolean>(false);
  firstOption = input<LookupViewModel | undefined>();
  control!: FormControl;
  disabledSignal = input<boolean>(false);

  valueChange = output<string>();

  private valueChangesSubscription!: Subscription;


  constructor(private injector: Injector) {
    super();
  }

  ngOnInit(): void {
    // Apply disabledSignal state on init
    if (this.disabledSignal()) {
      this.control?.disable();
    }
  }

  ngAfterContentInit(): void {
    const ngControl: NgControl = this.injector.get(NgControl, null) as NgControl;
    if (ngControl) {
      this.control = ngControl.control as FormControl;
    } else {
      this.control = new FormControl();
    }
  
    if (this.control) {
      this.valueChangesSubscription = this.control.valueChanges.subscribe((value) => {
        this.valueChange?.emit(value);
      });

      // Sync disabledSignal with control
      if (this.disabledSignal()) {
        this.control.disable();
      }
    }
  
    // Check for the default option and set it as the selected value
    if (this.options()) {
      const defaultOption = this.options()?.find(option => option.isDefault);
      if (defaultOption) {
        this.control.setValue(defaultOption.id);
      }
    }
  }

  ngOnDestroy(): void {
    // Clean up the subscription when component is destroyed
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
  }
}
